import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../api";

export function useEnvironmentFiles(envId) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadedEnvRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!envId) { setFiles([]); loadedEnvRef.current = null; return; }
    // Show a loading state only on the FIRST load for an environment. Later
    // refreshes update silently in the background so the list never flashes
    // empty — it just gains/loses entries when the fetch resolves.
    const isInitial = loadedEnvRef.current !== envId;
    if (isInitial) { setLoading(true); setFiles([]); }
    try {
      const list = await api.listEnvironmentFiles(envId);
      setFiles(list);
      setError(null);
      loadedEnvRef.current = envId;
    } catch (e) {
      setError(e);
    } finally {
      if (isInitial) setLoading(false);
    }
  }, [envId]);

  useEffect(() => { refresh(); }, [refresh]);

  const readFile = useCallback(async (path) => {
    if (!envId) return null;
    return api.readEnvironmentFile(envId, path);
  }, [envId]);

  const writeFile = useCallback(async (path, content) => {
    if (!envId) return null;
    const out = await api.writeEnvironmentFile(envId, path, content);
    await refresh();
    return out;
  }, [envId, refresh]);

  const deleteFile = useCallback(async (path) => {
    if (!envId) return;
    await api.deleteEnvironmentFile(envId, path);
    await refresh();
  }, [envId, refresh]);

  return { files, loading, error, refresh, readFile, writeFile, deleteFile };
}
